import { useTranslations } from '@/hooks/useTranslations';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/shadcn/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/shadcn/sheet';
import { ArrowDownWideNarrow, Grid3x3, Loader2, Search, SquareChartGantt, Table2, X } from 'lucide-react';
import { Fragment, useState } from 'react';
import { Checkbox } from '@/components/ui/shadcn/checkbox';
import { ScrollArea } from '@/components/ui/shadcn/scroll-area';
import type { ReactNode } from 'react';
import type { ToolbarFilterOption } from './FilterPanel';
import { buildFilterDefinitions, type ExtraViewMode, type ToolbarViewMode } from './GenericToolbar';

interface GenericToolbarMobileProps {
    quickFilterButtonLabel: string;
    quickFilterDefinitions: Array<{ id: string; label: string }>;
    columnDefinitions: Array<{ id: string; label: string }>;
    columnFilterOptions: Record<string, ToolbarFilterOption[]>;
    columnFilterLoading?: Record<string, boolean>;
    columnFilters: Record<string, string[]>;
    columnFilterSearch: Record<string, string>;
    setColumnFilterSearch: React.Dispatch<React.SetStateAction<Record<string, string>>>;
    toggleSingleFilter: (columnId: string, value: string) => void;
    handleFilterChange: (column: string, values: string[]) => void;
    clearFilters: () => void;
    sortColumn: string | null;
    sortDirection: 'asc' | 'desc' | null;
    handleSortChange: (column: string, direction: 'asc' | 'desc') => void;
    clearSort: () => void;
    search: string;
    setSearch: (value: string) => void;
    perPage?: number;
    onPerPageChange?: (perPage: number) => void;
    viewMode: ToolbarViewMode;
    setViewMode: (mode: ToolbarViewMode) => void;
    showGanttViewToggle?: boolean;
    showViewToggle?: boolean;
    extraViewMode?: ExtraViewMode;
    showPerPageSelect?: boolean;
    filteredCount: number;
    resourceName: string;
    resourceNamePlural: string;
    sortableColumns?: Array<{ id: string; label: string }>;
    excludeFromMoreFilters?: string[];
    extraFilterDefinitions?: Array<{ id: string; label: string }>;
    customFilters?: ReactNode;
    /** Where customFilters sits relative to the checkbox groups. */
    customFiltersPosition?: 'start' | 'end';
    inlineFilters?: ReactNode;
    showMoreFilters?: boolean;
}

export function GenericToolbarMobile({
    quickFilterButtonLabel,
    quickFilterDefinitions,
    columnDefinitions,
    columnFilterOptions,
    columnFilterLoading,
    columnFilters,
    columnFilterSearch,
    setColumnFilterSearch,
    toggleSingleFilter,
    handleFilterChange,
    clearFilters,
    sortColumn,
    sortDirection,
    handleSortChange,
    clearSort,
    search,
    setSearch,
    perPage,
    onPerPageChange,
    viewMode,
    setViewMode,
    filteredCount,
    resourceName,
    resourceNamePlural,
    sortableColumns,
    excludeFromMoreFilters = [],
    extraFilterDefinitions = [],
    customFilters,
    customFiltersPosition = 'start',
    inlineFilters,
    showMoreFilters = true,
    showGanttViewToggle = false,
    showViewToggle = true,
    extraViewMode,
    showPerPageSelect = true,
}: GenericToolbarMobileProps) {
    const { t } = useTranslations();
    const translationKey = `${resourceName}.index`;
    const [filterSheetOpen, setFilterSheetOpen] = useState(false);
    const [sortSheetOpen, setSortSheetOpen] = useState(false);
    const [activeFilterSection, setActiveFilterSection] = useState<string | null>(null);

    const allFilterColumns = buildFilterDefinitions({
        quickFilterDefinitions,
        columnDefinitions,
        extraFilterDefinitions,
        excludeFromMoreFilters,
        showMoreFilters,
    });

    const hasActiveFilters = Object.values(columnFilters).some((filters) => filters.length > 0);

    return (
        <div className="mb-4 flex flex-col gap-3 rounded-xl border border-border bg-card px-3 py-3">
            {/* Top row: Quick actions */}
            <div className="flex items-center gap-2">
                <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
                    <SheetTrigger asChild>
                        <Button
                            variant={hasActiveFilters ? 'default' : 'outline'}
                            size="sm"
                            title={quickFilterButtonLabel}
                            className={`h-10 flex-1 rounded-md px-3 ${hasActiveFilters ? '' : 'border-primary/40 text-primary hover:bg-primary/10 hover:text-primary'}`}
                        >
                            {hasActiveFilters ? t('common.filtered', 'Filtered') : t('common.filters', 'Filters')}
                            {hasActiveFilters && (
                                <span className="ml-1 rounded-sm bg-primary-foreground/20 px-1.5 py-0.5 text-[11px] leading-none">
                                    {Object.values(columnFilters).reduce((count, values) => count + values.length, 0)}
                                </span>
                            )}
                        </Button>
                    </SheetTrigger>
                    <SheetContent side="bottom" className="h-[85vh] rounded-t-lg p-0">
                        <div className="px-4 pt-6 pb-4">
                            <SheetHeader>
                                <SheetTitle className="text-2xl">{t('common.filters', 'Filters')}</SheetTitle>
                                <SheetDescription>{t('common.filter_description', 'Filter records by different criteria')}</SheetDescription>
                            </SheetHeader>
                        </div>
                        <ScrollArea className="h-[calc(85vh-140px)] px-4">
                            <div className="space-y-3 pb-4">
                                {customFilters && customFiltersPosition === 'start' && (
                                    <div className="rounded-md border bg-muted/30 p-3">{customFilters}</div>
                                )}
                                {allFilterColumns.map((column) => {
                                    const options = columnFilterOptions[column.id] || [];
                                    const selected = columnFilters[column.id] || [];
                                    const isLoading = !!columnFilterLoading?.[column.id];
                                    // Keep the section visible while its options load instead of popping it in late.
                                    if (!options.length && !isLoading) return null;
                                    const maxVisibleOptions = 10;
                                    const hasMoreThanTen = options.length > maxVisibleOptions;
                                    const searchValue = columnFilterSearch[column.id] || '';
                                    const isSearching = searchValue.length > 0;
                                    
                                    // Show first 10 if not searching, otherwise show all filtered results
                                    const displayedOptions = isSearching
                                        ? options.filter((option) => option.label.toLowerCase().includes(searchValue.toLowerCase()))
                                        : options.slice(0, maxVisibleOptions);
                                    
                                    const isExpanded = activeFilterSection === column.id;

                                    return (
                                        <div key={column.id} className="space-y-2">
                                            <button
                                                type="button"
                                                onClick={() => setActiveFilterSection(isExpanded ? null : column.id)}
                                                className="flex w-full items-center justify-between rounded-md border bg-card px-4 py-3.5 text-left transition-colors hover:bg-accent"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium text-base">{column.label}</span>
                                                    {selected.length > 0 && (
                                                        <span className="h-2 w-2 rounded-full bg-primary" aria-hidden />
                                                    )}
                                                </div>
                                                <span className="text-muted-foreground text-lg">{isExpanded ? '−' : '+'}</span>
                                            </button>
                                            {isExpanded && (
                                                <div className="ml-2 space-y-3 rounded-md border bg-muted/30 p-3">
                                                    {(hasMoreThanTen || isSearching) && (
                                                        <div>
                                                            <Input
                                                                placeholder={t('common.search_placeholder', 'Search...')}
                                                                value={searchValue}
                                                                onChange={(e) =>
                                                                    setColumnFilterSearch((prev) => ({
                                                                        ...prev,
                                                                        [column.id]: e.target.value,
                                                                    }))
                                                                }
                                                                className="h-10"
                                                            />
                                                        </div>
                                                    )}
                                                    {displayedOptions.length === 0 ? (
                                                        isLoading ? (
                                                            <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                                                                <Loader2 className="h-4 w-4 animate-spin" />
                                                                {t('common.loading', 'Loading...')}
                                                            </div>
                                                        ) : (
                                                            <p className="text-sm text-muted-foreground py-2">{t(`${translationKey}.toolbar.no_matches`, 'No matches')}</p>
                                                        )
                                                    ) : (
                                                        <div className="space-y-1.5">
                                                            {displayedOptions.map((option, index) => {
                                                                const checked = selected.includes(option.value);
                                                                // Heading wherever the group changes; see FilterPanel.
                                                                const showGroupHeading =
                                                                    !!option.group && option.group !== displayedOptions[index - 1]?.group;
                                                                return (
                                                                    <Fragment key={option.value}>
                                                                        {showGroupHeading && (
                                                                            <p className="text-muted-foreground px-3 pt-2 pb-0.5 text-xs font-semibold tracking-wide">
                                                                                {option.group}
                                                                            </p>
                                                                        )}
                                                                        <div className="flex items-center space-x-3 rounded-md px-3 py-2.5 transition-colors hover:bg-accent">
                                                                            <Checkbox
                                                                                id={`${column.id}-${option.value}`}
                                                                                checked={checked}
                                                                                onCheckedChange={() => toggleSingleFilter(column.id, option.value)}
                                                                                className="h-5 w-5"
                                                                            />
                                                                            <Label
                                                                                htmlFor={`${column.id}-${option.value}`}
                                                                                className="flex-1 cursor-pointer text-sm font-normal"
                                                                            >
                                                                                {option.label}
                                                                            </Label>
                                                                        </div>
                                                                    </Fragment>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                    {!isSearching && hasMoreThanTen && (
                                                        <p className="text-xs text-muted-foreground px-1 py-1">
                                                            {t(
                                                                `${translationKey}.toolbar.showing_first`,
                                                                `Showing ${maxVisibleOptions}. Search to see more.`
                                                            )}
                                                        </p>
                                                    )}
                                                    {selected.length > 0 && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleFilterChange(column.id, [])}
                                                            className="w-full text-destructive h-9"
                                                        >
                                                            <X className="mr-2 h-4 w-4" />
                                                            {t(`${translationKey}.toolbar.clear_filter`, 'Clear filter')} {column.label}
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {customFilters && customFiltersPosition === 'end' && (
                                    <div className="rounded-md border bg-muted/30 p-3">{customFilters}</div>
                                )}
                            </div>
                        </ScrollArea>
                        <div className="px-4 pt-3 pb-4 flex gap-2 border-t">
                            <Button variant="outline" onClick={clearFilters} className="flex-1 h-11">
                                <X className="mr-2 h-4 w-4" />
                                {t('common.clear', 'Clear')}
                            </Button>
                            <Button onClick={() => setFilterSheetOpen(false)} className="flex-1 h-11">
                                {t('common.apply_filters', 'Apply filters')}
                            </Button>
                        </div>
                    </SheetContent>
                </Sheet>

                {inlineFilters}

                {sortableColumns && sortableColumns.length > 0 && (
                    <Sheet open={sortSheetOpen} onOpenChange={setSortSheetOpen}>
                        <SheetTrigger asChild>
                            <Button variant="outline" size="sm" className="h-10 rounded-md border-border bg-background text-foreground">
                                <ArrowDownWideNarrow className="h-4 w-4" />
                                {t(`${translationKey}.toolbar.sort`, 'Sort')}
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="bottom" className="h-auto max-h-[60vh] rounded-t-lg p-0">
                            <div className="px-4 pt-6 pb-4">
                                <SheetHeader>
                                    <SheetTitle className="text-2xl">{t(`${translationKey}.toolbar.sort_by`, 'Sort by')}</SheetTitle>
                                    <SheetDescription>{t(`${translationKey}.toolbar.sort_description`, `Choose how to sort ${resourceNamePlural}`)}</SheetDescription>
                                </SheetHeader>
                            </div>
                            <div className="px-4 space-y-2 pb-4">
                                {sortableColumns.map((item) => (
                                    <div
                                        key={item.id}
                                        className="flex items-center justify-between rounded-md border bg-card px-4 py-3.5"
                                    >
                                        <span className="text-base font-medium">{item.label}</span>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant={sortColumn === item.id && sortDirection === 'asc' ? 'default' : 'outline'}
                                                size="sm"
                                                className="h-9 w-9"
                                                onClick={() => {
                                                    handleSortChange(item.id, 'asc');
                                                    setSortSheetOpen(false);
                                                }}
                                            >
                                                ↑
                                            </Button>
                                            <Button
                                                variant={sortColumn === item.id && sortDirection === 'desc' ? 'default' : 'outline'}
                                                size="sm"
                                                className="h-9 w-9"
                                                onClick={() => {
                                                    handleSortChange(item.id, 'desc');
                                                    setSortSheetOpen(false);
                                                }}
                                            >
                                                ↓
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {sortColumn && (
                                <div className="px-4 pt-3 pb-4 border-t">
                                    <Button variant="outline" onClick={clearSort} className="w-full h-11">
                                        {t(`${translationKey}.toolbar.clear_sort`, 'Clear sort')}
                                    </Button>
                                </div>
                            )}
                        </SheetContent>
                    </Sheet>
                )}
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                    className="h-10 w-full rounded-md border-border bg-background pl-9"
                    placeholder={t(`${translationKey}.toolbar.search_placeholder`, 'Search...')}
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                />
            </div>

            {/* Bottom row: Results, per-page, and view toggle */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm text-muted-foreground">
                    {filteredCount} {resourceNamePlural}{filteredCount !== 1 ? '' : ''} {t(`${translationKey}.toolbar.found`, 'found')}
                </p>
                {showPerPageSelect && perPage !== undefined && onPerPageChange ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        {t(`${translationKey}.toolbar.per_page`, 'Per page')}:
                        <Select value={String(perPage)} onValueChange={(value) => onPerPageChange(Number(value))}>
                            <SelectTrigger className="h-9 w-20 rounded-md border-border bg-background">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {[10, 25, 50, 100].map((option) => (
                                    <SelectItem key={option} value={String(option)}>
                                        {option}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                ) : null}
                {showViewToggle && (
                    <div className="flex items-center gap-0.5 rounded-md bg-muted p-1">
                        <Button
                            variant={viewMode === 'table' ? 'secondary' : 'ghost'}
                            size="sm"
                            className="h-8 w-8 rounded-sm px-0"
                            onClick={() => setViewMode('table')}
                            aria-label={t(`${translationKey}.toolbar.view_table`, 'Table')}
                        >
                            <Table2 className="h-4 w-4" />
                        </Button>
                        <Button
                            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                            size="sm"
                            className="h-8 w-8 rounded-sm px-0"
                            onClick={() => setViewMode('grid')}
                            aria-label={t(`${translationKey}.toolbar.view_grid`, 'Grid')}
                        >
                            <Grid3x3 className="h-4 w-4" />
                        </Button>
                        {showGanttViewToggle ? (
                            <Button
                                variant={viewMode === 'gantt' ? 'secondary' : 'ghost'}
                                size="sm"
                                className="h-8 w-8 rounded-sm px-0"
                                onClick={() => setViewMode('gantt')}
                                title={t(`${translationKey}.toolbar.view_gantt`, 'Gantt')}
                                aria-label={t(`${translationKey}.toolbar.view_gantt`, 'Gantt')}
                            >
                                <SquareChartGantt className="h-4 w-4" />
                            </Button>
                        ) : null}
                        {extraViewMode ? (
                            <Button
                                variant={viewMode === extraViewMode.value ? 'secondary' : 'ghost'}
                                size="sm"
                                className="h-8 w-8 rounded-sm px-0"
                                onClick={() => setViewMode(extraViewMode.value)}
                                title={extraViewMode.label}
                                aria-label={extraViewMode.label}
                            >
                                {extraViewMode.icon}
                            </Button>
                        ) : null}
                    </div>
                )}
            </div>
        </div>
    );
}
